import { Descriptions, Flex, Tag, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import { TeamOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ConversationCompletionDetail } from '@/api/chat/interfaces';
import { formatDurationDisplay } from './constants';
import { FlagTag, HelpedTag, YesNoText } from './tags';
import { formatWhen } from '@/utils/dateFormat';

const { Text, Paragraph } = Typography;

export function CompletionDetails({ completion }: { completion: ConversationCompletionDetail }) {
  const { t } = useTranslation('admin');
  const duration = formatDurationDisplay(completion.channel, completion.durationMs, t);
  const contact = [completion.contact?.name, completion.contact?.phone, completion.contact?.email]
    .filter(Boolean)
    .join(' · ');
  const items: DescriptionsProps['items'] = [
    {
      key: 'started',
      label: t('conversationOverview.detail.started'),
      children: formatWhen(completion.startedAt)
    },
    {
      key: 'ended',
      label: t('conversationOverview.detail.ended'),
      children: formatWhen(completion.endedAt)
    },
    {
      key: 'duration',
      label: t('conversationOverview.columns.duration'),
      children: duration.text
    },
    {
      key: 'helped',
      label: t('conversationOverview.detail.customerHelped'),
      children: <HelpedTag value={completion.customerHelped} />
    },
    {
      key: 'technical',
      label: t('conversationOverview.columns.technicalIssue'),
      children: <YesNoText value={completion.technicalIssue} />
    },
    {
      key: 'delay',
      label: t('conversationOverview.columns.delay'),
      children: <YesNoText value={completion.assistantResponseDelayed} />
    },
    {
      key: 'callback',
      label: t('conversationOverview.columns.callbackRequested'),
      children: <YesNoText value={completion.callbackRequested} />
    },
    {
      key: 'handoff',
      label: t('conversationOverview.columns.humanHandoff'),
      children: completion.humanHandoff ? (
        <Tag icon={<TeamOutlined />} color="warning">
          {t('conversationOverview.yes')}
        </Tag>
      ) : (
        <Text type="secondary">{t('conversationOverview.no')}</Text>
      )
    },
    {
      key: 'flag',
      label: t('conversationOverview.columns.flag'),
      children: <FlagTag flag={completion.flag} />
    }
  ];
  if (completion.technicalIssues?.length) {
    items.push({
      key: 'reasons',
      label: t('conversationOverview.detail.technicalIssues'),
      children: (
        <Flex gap={4} wrap="wrap">
          {completion.technicalIssues.map((issue) => (
            <Tag key={issue} icon={<WarningOutlined />} color="orange">
              {issue}
            </Tag>
          ))}
        </Flex>
      )
    });
  }
  if (completion.summary) {
    items.push({
      key: 'summary',
      label: t('conversationOverview.detail.summary'),
      children: <Paragraph style={{ marginBottom: 0 }}>{completion.summary}</Paragraph>
    });
  }
  if (contact) {
    items.push({
      key: 'contact',
      label: t('conversationOverview.detail.contact'),
      children: contact
    });
  }
  return <Descriptions column={1} size="small" items={items} />;
}
